/**
 * OptimizationParamsForm - Parameters for day-ahead optimization.
 *
 * Props:
 *   - params: Object with optimization parameters
 *   - onChange: Callback(updatedParams) when any field changes
 *
 * Includes:
 *   - Optimization objective selection (radio buttons)
 *   - What-if load/tariff scaling (applied to profiles before MILP solve)
 *   - New asset addition forms (PV, wind, battery)
 *
 * Note: No flex load — no backend model exists for adding new flex
 * loads via this form. Existing flex loads (heat pumps, EVs) attached
 * to buildings are handled automatically by the optimizer.
 */

import React, { useState } from 'react'
import { Form, Row, Col, Card, Badge } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

function OptimizationParamsForm({ params, onChange }) {
  const { t } = useTranslation()
  const [localParams, setLocalParams] = useState(params || {
    objective: 'min_cost',
    loadGrowthPct: 0,
    tariffChangePct: 0,
    addPvSystem: false,
    newPvCapacityKwp: 50,
    addWindTurbine: false,
    newWindCapacityKw: 25,
    addBattery: false,
    newBatteryCapacityKwh: 100,
    newBatteryChargeRateKw: 50,
    newBatteryDischargeRateKw: 50,
    newBatteryEfficiencyPct: 90,
  })

  const handleFieldChange = (field, value) => {
    const updated = { ...localParams, [field]: value }
    setLocalParams(updated)
    if (onChange) onChange(updated)
  }

  return (
    <div className="optimization-params-form">
      {/* Optimization Objective */}
      <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)' }}>
        <Card.Body>
          <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>
            {t('optimization_params.objective_title')}
          </h6>
          <Form.Check
            type="radio"
            id="opt-obj-min-cost"
            label={t('optimization_params.obj_min_cost')}
            name="opt-objective"
            value="min_cost"
            checked={localParams.objective === 'min_cost'}
            onChange={(e) => handleFieldChange('objective', e.target.value)}
            className="mb-2"
          />
          <Form.Text className="d-block mb-3 ms-4" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
            {t('optimization_params.obj_min_cost_desc')}
          </Form.Text>

          <Form.Check
            type="radio"
            id="opt-obj-max-self-suff"
            label={t('optimization_params.obj_max_self_sufficiency')}
            name="opt-objective"
            value="max_self_sufficiency"
            checked={localParams.objective === 'max_self_sufficiency'}
            onChange={(e) => handleFieldChange('objective', e.target.value)}
            className="mb-2"
          />
          <Form.Text className="d-block mb-3 ms-4" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
            {t('optimization_params.obj_max_self_sufficiency_desc')}
          </Form.Text>

          <Form.Check
            type="radio"
            id="opt-obj-max-self-cons"
            label={t('optimization_params.obj_max_self_consumption')}
            name="opt-objective"
            value="max_self_consumption"
            checked={localParams.objective === 'max_self_consumption'}
            onChange={(e) => handleFieldChange('objective', e.target.value)}
            className="mb-2"
          />
          <Form.Text className="d-block ms-4" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
            {t('optimization_params.obj_max_self_consumption_desc')}
          </Form.Text>
        </Card.Body>
      </Card>

      {/* What-If Parameter Sliders */}
      <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)' }}>
        <Card.Body>
          <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>
            {t('optimization_params.whatif_title')}
          </h6>
          <Form.Text className="text-muted d-block mb-3">
            {t('optimization_params.whatif_help')}
          </Form.Text>

          {/* Load Scaling */}
          <Form.Group className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <Form.Label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 0 }}>
                {t('optimization_params.load_scaling')}
              </Form.Label>
              <Badge bg="secondary">{localParams.loadGrowthPct > 0 ? '+' : ''}{localParams.loadGrowthPct}%</Badge>
            </div>
            <Form.Range
              min={-30}
              max={50}
              step={5}
              value={localParams.loadGrowthPct}
              onChange={(e) => handleFieldChange('loadGrowthPct', parseInt(e.target.value))}
            />
            <div className="d-flex justify-content-between" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)' }}>
              <span>{t('optimization_params.scale_low')}</span>
              <span>{t('optimization_params.scale_mid')}</span>
              <span>{t('optimization_params.scale_high')}</span>
            </div>
          </Form.Group>

          {/* Tariff Change */}
          <Form.Group className="mb-0">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <Form.Label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 0 }}>
                {t('optimization_params.tariff_change')}
              </Form.Label>
              <Badge bg="secondary">{localParams.tariffChangePct > 0 ? '+' : ''}{localParams.tariffChangePct}%</Badge>
            </div>
            <Form.Range
              min={-30}
              max={50}
              step={5}
              value={localParams.tariffChangePct}
              onChange={(e) => handleFieldChange('tariffChangePct', parseInt(e.target.value))}
            />
            <div className="d-flex justify-content-between" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)' }}>
              <span>{t('optimization_params.scale_low')}</span>
              <span>{t('optimization_params.scale_mid')}</span>
              <span>{t('optimization_params.scale_high')}</span>
            </div>
          </Form.Group>
        </Card.Body>
      </Card>

      {/* New Asset Addition */}
      <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)' }}>
        <Card.Body>
          <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>
            {t('optimization_params.new_asset_title')}
          </h6>

          {/* Add PV System */}
          <Form.Check
            type="switch"
            id="opt-add-pv-system"
            label={t('optimization_params.add_pv')}
            checked={localParams.addPvSystem}
            onChange={(e) => handleFieldChange('addPvSystem', e.target.checked)}
            className="mb-2"
          />
          {localParams.addPvSystem && (
            <Row className="g-3 mb-3 ms-4">
              <Col md={6}>
                <Form.Label style={{ fontSize: 'var(--font-size-sm)' }}>
                  {t('optimization_params.pv_capacity')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={500}
                  step={5}
                  value={localParams.newPvCapacityKwp}
                  onChange={(e) => handleFieldChange('newPvCapacityKwp', parseFloat(e.target.value))}
                />
                <Form.Text className="text-muted">{t('optimization_params.pv_capacity_help')}</Form.Text>
              </Col>
            </Row>
          )}

          {/* Add Wind Turbine */}
          <Form.Check
            type="switch"
            id="opt-add-wind-turbine"
            label={t('optimization_params.add_wind')}
            checked={localParams.addWindTurbine}
            onChange={(e) => handleFieldChange('addWindTurbine', e.target.checked)}
            className="mb-2"
          />
          {localParams.addWindTurbine && (
            <Row className="g-3 mb-3 ms-4">
              <Col md={6}>
                <Form.Label style={{ fontSize: 'var(--font-size-sm)' }}>
                  {t('optimization_params.wind_capacity')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={100}
                  step={5}
                  value={localParams.newWindCapacityKw}
                  onChange={(e) => handleFieldChange('newWindCapacityKw', parseFloat(e.target.value))}
                />
                <Form.Text className="text-muted">{t('optimization_params.wind_capacity_help')}</Form.Text>
              </Col>
            </Row>
          )}

          {/* Add Battery */}
          <Form.Check
            type="switch"
            id="opt-add-battery"
            label={t('optimization_params.add_battery')}
            checked={localParams.addBattery}
            onChange={(e) => handleFieldChange('addBattery', e.target.checked)}
            className="mb-2"
          />
          {localParams.addBattery && (
            <Row className="g-3 mb-3 ms-4">
              <Col md={3}>
                <Form.Label style={{ fontSize: 'var(--font-size-sm)' }}>
                  {t('optimization_params.batt_capacity')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min={10}
                  max={1000}
                  step={10}
                  value={localParams.newBatteryCapacityKwh}
                  onChange={(e) => handleFieldChange('newBatteryCapacityKwh', parseFloat(e.target.value))}
                />
                <Form.Text className="text-muted">{t('optimization_params.batt_kwh')}</Form.Text>
              </Col>
              <Col md={3}>
                <Form.Label style={{ fontSize: 'var(--font-size-sm)' }}>
                  {t('optimization_params.batt_charge_rate')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={500}
                  step={5}
                  value={localParams.newBatteryChargeRateKw}
                  onChange={(e) => handleFieldChange('newBatteryChargeRateKw', parseFloat(e.target.value))}
                />
                <Form.Text className="text-muted">{t('optimization_params.batt_kw')}</Form.Text>
              </Col>
              <Col md={3}>
                <Form.Label style={{ fontSize: 'var(--font-size-sm)' }}>
                  {t('optimization_params.batt_discharge_rate')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={500}
                  step={5}
                  value={localParams.newBatteryDischargeRateKw}
                  onChange={(e) => handleFieldChange('newBatteryDischargeRateKw', parseFloat(e.target.value))}
                />
                <Form.Text className="text-muted">{t('optimization_params.batt_kw')}</Form.Text>
              </Col>
              <Col md={3}>
                <Form.Label style={{ fontSize: 'var(--font-size-sm)' }}>
                  {t('optimization_params.batt_efficiency')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min={70}
                  max={99}
                  step={1}
                  value={localParams.newBatteryEfficiencyPct}
                  onChange={(e) => handleFieldChange('newBatteryEfficiencyPct', parseFloat(e.target.value))}
                />
                <Form.Text className="text-muted">{t('optimization_params.batt_eff_help')}</Form.Text>
              </Col>
            </Row>
          )}

        </Card.Body>
      </Card>
    </div>
  )
}

export default OptimizationParamsForm
